<template>
  <div class="relative" ref="dropdownRef" :class="{ 'opacity-50': disabled === 'true' }">
    <!-- Input Field -->
    <input
      type="text"
      v-model="inputValue"
      :disabled="disabled === 'true'"
      @focus="isOpen = true"
      @input="onInput"
      @change="emitAutofillChange"
      :placeholder="placeholder"
      class="w-full p-3 rounded-lg bg-dark-grey text-total-white focus:outline-none focus:ring-2 focus:ring-prime-golden"
    />
    <div
      v-if="showChevron && !isOpen"
      class="absolute right-3 top-1/2 transform -translate-y-1/2 text-total-white-800 mr-1"
    >
      <i class="fa-solid fa-chevron-down"></i>
    </div>
    <!-- Suggestions Dropdown -->
    <div
      v-if="isOpen && filteredOptions.length > 0"
      class="absolute mt-1 w-full text-left rounded-md shadow-lg bg-dark-grey z-10 max-h-52 overflow-auto border border-gray-600"
    >
      <ul class="py-1">
        <li
          v-for="option in filteredOptions"
          :key="option.value"
          @click="(event) => handleOptionClick(event, option)"
          class="cursor-pointer px-4 py-2 border-b border-light-grey-translucent text-total-white"
        >
          {{ option.label }}
          <div v-if="option.sublabel" class="text-xs text-gray-400">
            {{ option.sublabel }}
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { customFilterAndSort } from "@/misc/util";
import {
  ref,
  computed,
  defineProps,
  defineEmits,
  onMounted,
  onBeforeUnmount,
  watch,
} from "vue";

const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  modelValue: {
    type: [String, Number],
    default: "",
  },
  placeholder: {
    type: String,
    default: "Select an option",
  },
  allowOtherValues: {
    type: Boolean,
    default: false,
  },
  showChevron: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits(["update:modelValue", "update:label"]);

const isOpen = ref(false);
const inputValue = ref("");
const dropdownRef = ref(null);

// Helper to get the option by value
const getOptionByValue = (value) => {
  return props.options.find((option) => option.value === value);
};

const onInput = () => {
  isOpen.value = true;
};

// Select an option from the dropdown
const selectOption = (option) => {
  inputValue.value = option.label;
  emits("update:modelValue", option.value);
  emits("update:label", option.label);

  isOpen.value = false;
};
const handleOptionClick = (event, option) => {
  if (option.onClick) {
    // Prevent dropdown from closing if the option has an onClick handler
    option.onClick(event);
  } else {
    selectOption(option); // Select option normally
  }
};
// Compute the filtered options based on the input value
const filteredOptions = computed(() => {
  return customFilterAndSort(props.options, inputValue.value);
});

const emitAutofillChange = () => {
  setTimeout(() => {
    if (props.allowOtherValues && inputValue.value) {
      emits("update:modelValue", inputValue.value); // Allow custom value
    }
    handleAutofill();
  }, 200);
};

const handleAutofill = () => {
  const matchedOption = props.options.find(
    (option) => option.label.toLowerCase() === inputValue.value.toLowerCase()
  );

  if (matchedOption) {
    selectOption(matchedOption);
  } else if (props.allowOtherValues) {
    // Allow custom value
    emits("update:modelValue", inputValue.value);
  }
};

// Watch for changes in modelValue to update inputValue
watch(
  () => props.modelValue,
  (newValue) => {
    const selectedOption = getOptionByValue(newValue);

    if (props.allowOtherValues) {
      inputValue.value = selectedOption ? selectedOption.label : newValue;
    } else {
      inputValue.value = selectedOption ? selectedOption.label : "";
    }
  },
  { immediate: true }
);

watch(inputValue, (newValue) => {
  if (props.allowOtherValues) {
    emits("update:modelValue", newValue);
  }
});

// Close dropdown when clicking outside
const handleClickOutside = (event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    isOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>
